/**
 * @file Weather Context Provider
 * @description The header answer for the garden being tended, shared with every place that shows the header or the
 * weather now (W24 Job D). Kept by its old name, WeatherProvider, so the route tree does not change.
 *
 * It reads one small answer, GET /header (api/header): the frost bar's alert (the Today brief's own words), the weather
 * now (B77: the reading, the sky's words and icon key, the period's name, all in the reader's unit and language), the
 * icon the header's pill draws, the "no forecast yet" sentence (B44) and keepMinutes. It used to read the raw forecast
 * (GET /weather/forecast/user, 13 KB) on every screen and the header read the whole Today brief (33 KB) for one bar; the
 * Weather page now reads its own days (features/weather useWeatherDays).
 *
 * Kept under [HEADER_QUERY_KEY, garden, language, unit] (['header-answer', 177, 'es', 'F']): the garden being tended
 * (chosenGarden, else the person's own, as the brief's key), so in a shared garden it is the owner's garden and place; the
 * language and the unit, so a switch reads it again. Asked only when the garden place says Grow can plan from its postcode
 * (useGardenPostcode: the owner's in a shared garden); this provider sits above the finish-signing-up screen, and asking
 * before a postcode got a 400 and a retry while a new gardener typed theirs. A new postcode reads it again (the key holds
 * the garden, not the postcode).
 *
 * How long the phone keeps it is the server's: keepMinutes (the Grow setting forecast.KEEP_MINUTES) is the read's stale time
 * and its interval while the app is open; before the first answer, the app's default stale time and no interval. A garden
 * write reads it again at once (lib/briefFreshness gardenWritten), and so does a new Today brief held on the phone (its
 * fingerprint changed: a to-do tick's answer, which can end the frost job, or a re-read), so the frost bar and the pill
 * never outlive the Today card's alert.
 *
 * Mounted once by the route tree around the signed-in pages; read with shared/hooks/useWeatherContext (AlertBar,
 * WeatherPill, useWeatherNow, TodayPage, useWeatherDays).
 *
 * @module context/WeatherContext
 */

import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient, type Query } from '@tanstack/react-query';
import { getHeader, type HeaderAnswer } from '../api/header';
import { chosenGarden } from '../api/gardenMembers';
import { WeatherContext } from './WeatherContextDefinition';
import { useGardenPostcode } from '../hooks/useGardenPostcode';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../hooks/useAuth';
import { useUserConfig } from './userConfigStore';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';
import { briefKeyStart, HEADER_QUERY_KEY, type BriefStamp } from '../lib/briefFreshness';

/** A minute, in ms: keepMinutes is in minutes. */
const MINUTE_MS = 60 * 1000;

/** What the provider wraps: the rest of the app. */
type WeatherProviderProps = {
  /** The signed-in pages. */
  children: ReactNode;
};

/**
 * How long an answer is kept, from the answer itself: its keepMinutes in ms, or undefined before there is one.
 *
 * @param answer - The header answer held, or undefined.
 * @returns The ms, or undefined.
 */
function keptMs(answer: HeaderAnswer | undefined): number | undefined {
  return typeof answer?.keepMinutes === 'number' ? answer.keepMinutes * MINUTE_MS : undefined;
}

/**
 * WeatherProvider component
 * @component
 * @description Reads the header answer and shares it with every child.
 *
 * @param {object} props - Component props
 * @param {ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component
 */
export function WeatherProvider({ children }: WeatherProviderProps): JSX.Element {
  // The garden's postcode, and whether it can be planned, as the server says (the owner's in a shared garden).
  const { zip, usable } = useGardenPostcode();
  const { i18n: { language } } = useTranslation();
  const { mysqlUser } = useAuth();
  const { userConfig } = useUserConfig();
  const queryClient = useQueryClient();
  const unit = userConfig.preferences.temperatureUnit === 'C' ? 'C' : 'F';
  /** The garden being tended: the one chosen on this device, else the person's own (the brief's key's garden). */
  const gardenId = chosenGarden() ?? mysqlUser?.user_id ?? null;

  const {
    data: header,
    isLoading,
    error,
    refetch: refetchWeather,
  } = useQuery({
    // The language and the unit are in the key: the words and the reading come in them, so a switch reads them again.
    queryKey: [HEADER_QUERY_KEY, gardenId, language, unit],
    queryFn: () => getHeader(language, unit),
    enabled: usable && gardenId !== null,
    // The server's keepMinutes, once it has said; the app's default stale time and no interval before.
    staleTime: (query: Query<HeaderAnswer>) => keptMs(query.state.data) ?? QUERY_STALE_TIME_MS,
    refetchInterval: (query: Query<HeaderAnswer>) => keptMs(query.state.data) ?? false,
  });

  // The garden's postcode changed (saved on the profile or the ZIP step): the key does not hold it, so read again now.
  const zipSeen = useRef(zip);
  useEffect(() => {
    if (zipSeen.current === zip) return;
    const before = zipSeen.current;
    zipSeen.current = zip;
    // The first postcode (or the place arriving) turns the read on, which reads by itself.
    if (!before) return;
    void queryClient.invalidateQueries({ queryKey: [HEADER_QUERY_KEY] });
  }, [zip, queryClient]);

  // A held Today brief whose fingerprint changed (a tick's answer, a re-read): the alert may have changed, so read again.
  useEffect(() => {
    /** Each brief's last fingerprint, by its query's hash; the first answer is only noted. */
    const seen = new Map<string, string | null>();
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success' || event.query.queryKey[0] !== briefKeyStart()[0]) return;
      const print = (event.query.state.data as BriefStamp | undefined)?.fingerprint ?? null;
      const before = seen.get(event.query.queryHash);
      seen.set(event.query.queryHash, print);
      if (before !== undefined && before !== print) void queryClient.invalidateQueries({ queryKey: [HEADER_QUERY_KEY] });
    });
  }, [queryClient]);

  // One object per change, not per render: the header, Today, Plants and Weather read it.
  const value = useMemo(() => ({
    header,
    alert: header?.alert ?? null,
    now: header?.now ?? null,
    pillIcon: header?.pillIcon ?? null,
    noForecastMessage: header?.noForecastMessage ?? null,
    keepMinutes: header?.keepMinutes ?? null,
    isLoading,
    error: error as Error | null,
    refetchWeather,
  }), [header, isLoading, error, refetchWeather]);

  return (
    <WeatherContext.Provider value={value}>
      {children}
    </WeatherContext.Provider>
  );
}
